// src/types/notifications.ts - Types pour le système de notifications

import { formatNumber } from '../utils/numberFormat';
import i18n from '../i18n';

export type NotificationType = 
  | 'low_stock'      // Stock en dessous du seuil
  | 'out_of_stock'   // Stock à 0
  | 'appointment_reminder_24h'  // Rappel de rendez-vous 24h avant
  | 'appointment_reminder_2h'   // Rappel de rendez-vous 2h avant
  | 'appointment_request'  // Demande de rendez-vous depuis landing page
  | 'pickup_request'       // Demande de ramassage (marketplace / lien direct)
  | 'support_ticket_created'  // Nouveau ticket support (staff)
  | 'support_ticket_reply'    // Réponse NauvaTek sur un ticket (client)
  | 'support_ticket_resolved' // Ticket clôturé (client)
  | 'support_ticket_opened_for_you' // Ticket ouvert par NauvaTek pour un employé
  | 'birthday'       // Anniversaire client
  | 'inactive_client' // Client inactif
  | 'loyalty_milestone' // Seuil de fidélité atteint
  | 'payment_plan_reminder_2d'   // Paiement en plusieurs : échéance dans 2 jours
  | 'payment_plan_reminder_1d'   // Paiement en plusieurs : échéance demain
  | 'payment_plan_reminder_due' // Paiement en plusieurs : échéance aujourd'hui
  /** Rappel employé : saisir / valider son rapport d'activité avant deadline */
  | 'activity_report_submit_reminder'
  /** Manager (canViewAll) : un rapport vient d’être validé — à lire */
  | 'activity_report_validated'
  /** Manager : rapport non validé après échéance */
  | 'activity_report_overdue'
  /** Paiement en plusieurs : échéance dépassée, solde restant */
  | 'payment_plan_overdue'
  /** Licence Nauvatek : échéance dans 7 jours */
  | 'licence_payment_due_7d'
  /** Licence Nauvatek : échéance dans 1 jour */
  | 'licence_payment_due_1d'
  /** Licence Nauvatek : paiement en retard / bloqué */
  | 'licence_payment_blocked'
  /** Messages Clients : délai écoulé, prêt à envoyer WhatsApp */
  | 'messages_clients_ready'
  /** Messages Clients : client sans téléphone exploitable */
  | 'messages_clients_no_phone'
  /** OF fabrication passé en pause */
  | 'manufacturing_order_on_hold'
  /** OF fabrication terminé (à clôturer / stock) */
  | 'manufacturing_order_done'
  /** Inventaire physique démarré */
  | 'inventory_audit_started'
  /** Inventaire physique à clôturer */
  | 'inventory_audit_to_close'
  /** Perte de stock déclarée */
  | 'inventory_loss_created'
  /** Indicateur de performance : fiches du mois générées */
  | 'performance_sheets_generated'
  /** Indicateur de performance : fiche employé validée */
  | 'performance_sheet_validated';

/** Qui doit voir la notification (filtre UI). */
export type NotificationAudience = 'business' | 'nauvatek_staff' | 'user';

export type NotificationSeverity = 'warning' | 'critical' | 'info';

export interface Notification {
  id: string;
  type: NotificationType;
  severity: NotificationSeverity;
  businessId: string;
  
  // Message et métadonnées
  message: string;
  timestamp: Date;
  read: boolean;
  
  // Utilisateur qui a déclenché (optionnel)
  triggeredBy?: string;
  triggeredByUser?: string;

  /** Cible précise (ex. auteur du ticket). Si défini, seuls ce user + staff éventuellement. */
  recipientUserId?: string;
  /** audience: nauvatek_staff = SA/CSM uniquement ; user = recipientUserId ; business = tous (défaut) */
  audience?: NotificationAudience;
  
  // Données spécifiques selon le type (optionnelles)
  productId?: string;
  productName?: string;
  currentStock?: number;
  minStockAlert?: number;
  siteId?: string;
  siteName?: string;
  clientId?: string;
  clientName?: string;
  appointmentId?: string;
  appointmentRequestId?: string;
  pickupRequestId?: string;
  trackingId?: string;
  supportTicketId?: string;
  supportTicketSubject?: string;
  appointmentDate?: Date;
  appointmentTime?: string;
  appointmentTitle?: string;
  employeeName?: string;
  paymentPlanId?: string;
  targetDate?: Date;
  amountRemaining?: number;
  saleReceiptNumber?: string;
  activityReportId?: string;
  employeeId?: string;
  /** Fiche performanceObjectives (scorecard) */
  performanceObjectivesId?: string;
  /** Période scorecard (1–12 / année) */
  performanceMonth?: number;
  performanceYear?: number;
  /** Nombre de fiches créées (notif génération) */
  createdCount?: number;
  manufacturingOrderId?: string;
  inventorySessionId?: string;
  followUpMessageId?: string;
  dedupKey?: string;
}

// Alias pour la compatibilité
export type StockNotification = Notification;

export interface NotificationSummary {
  total: number;
  unread: number;
  byType: {
    low_stock: number;
    out_of_stock: number;
    appointment_reminder_24h: number;
    appointment_reminder_2h: number;
    appointment_request: number;
    pickup_request: number;
    support_ticket_created: number;
    support_ticket_reply: number;
    support_ticket_resolved: number;
    support_ticket_opened_for_you: number;
    birthday: number;
    inactive_client: number;
    loyalty_milestone: number;
    payment_plan_reminder_2d: number;
    payment_plan_reminder_1d: number;
    payment_plan_reminder_due: number;
    activity_report_submit_reminder: number;
    activity_report_validated: number;
    activity_report_overdue: number;
    payment_plan_overdue: number;
    licence_payment_due_7d: number;
    licence_payment_due_1d: number;
    licence_payment_blocked: number;
    messages_clients_ready: number;
    messages_clients_no_phone: number;
    manufacturing_order_on_hold: number;
    manufacturing_order_done: number;
    inventory_audit_started: number;
    inventory_audit_to_close: number;
    inventory_loss_created: number;
    performance_sheets_generated: number;
    performance_sheet_validated: number;
  };
  bySeverity: {
    warning: number;
    critical: number;
    info: number;
  };
}

// Configuration des notifications
export interface NotificationConfig {
  enabled: boolean;
  emailNotifications: boolean;
  smsNotifications: boolean;
  pushNotifications: boolean;
}

// Utilitaires pour les messages (écriture legacy Firestore — l'affichage passe par getNotificationMessage)
export const STOCK_NOTIFICATION_MESSAGES = {
  low_stock: (productName: string, currentStock: number) =>
    String(
      i18n.t('low_stock', {
        ns: 'notifications',
        productName,
        currentStock,
      })
    ),
  out_of_stock: (productName: string) =>
    String(i18n.t('out_of_stock', { ns: 'notifications', productName })),
};

export const BIRTHDAY_NOTIFICATION_MESSAGES = {
  birthday: (clientName: string) =>
    String(i18n.t('birthday', { ns: 'notifications', clientName })),
};

export const APPOINTMENT_NOTIFICATION_MESSAGES = {
  appointment_reminder_24h: (
    clientName: string,
    appointmentTitle: string,
    appointmentTime: string
  ) =>
    String(
      i18n.t('appointment_reminder_24h', {
        ns: 'notifications',
        clientName,
        appointmentTitle,
        appointmentTime,
      })
    ),
  appointment_reminder_2h: (
    clientName: string,
    appointmentTitle: string,
    appointmentTime: string
  ) =>
    String(
      i18n.t('appointment_reminder_2h', {
        ns: 'notifications',
        clientName,
        appointmentTitle,
        appointmentTime,
      })
    ),
};

export const PAYMENT_PLAN_NOTIFICATION_MESSAGES = {
  payment_plan_reminder_2d: (clientName: string, amountRemaining: number, receipt?: string) =>
    String(
      i18n.t('payment_plan_reminder_2d', {
        ns: 'notifications',
        clientName,
        amount: formatNumber(amountRemaining),
        receiptSuffix: receipt ? ` (${receipt})` : '',
      })
    ),
  payment_plan_reminder_1d: (clientName: string, amountRemaining: number, receipt?: string) =>
    String(
      i18n.t('payment_plan_reminder_1d', {
        ns: 'notifications',
        clientName,
        amount: formatNumber(amountRemaining),
        receiptSuffix: receipt ? ` (${receipt})` : '',
      })
    ),
  payment_plan_reminder_due: (clientName: string, amountRemaining: number, receipt?: string) =>
    String(
      i18n.t('payment_plan_reminder_due', {
        ns: 'notifications',
        clientName,
        amount: formatNumber(amountRemaining),
        receiptSuffix: receipt ? ` (${receipt})` : '',
      })
    ),
};

export const SUPPORT_TICKET_NOTIFICATION_MESSAGES = {
  support_ticket_created: (authorName: string, subject: string) =>
    String(
      i18n.t('support_ticket_created', {
        ns: 'notifications',
        authorName,
        subject,
      })
    ),
  support_ticket_reply: (subject: string) =>
    String(i18n.t('support_ticket_reply', { ns: 'notifications', subject })),
  support_ticket_resolved: (subject: string) =>
    String(i18n.t('support_ticket_resolved', { ns: 'notifications', subject })),
  support_ticket_opened_for_you: (subject: string) =>
    String(
      i18n.t('support_ticket_opened_for_you', { ns: 'notifications', subject })
    ),
};

// Couleurs pour l'interface
export const NOTIFICATION_COLORS = {
  low_stock: '#faad14',           // Jaune
  out_of_stock: '#ff4d4f',        // Rouge
  appointment_reminder_24h: '#1890ff', // Bleu
  appointment_reminder_2h: '#fa8c16',  // Orange
  appointment_request: '#52c41a', // Vert
  pickup_request: '#13c2c2', // Cyan
  support_ticket_created: '#E91E63',
  support_ticket_reply: '#d946ef',
  support_ticket_resolved: '#52c41a',
  support_ticket_opened_for_you: '#128C7E',
  birthday: '#52c41a',            // Vert
  inactive_client: '#faad14',     // Jaune
  loyalty_milestone: '#722ed1',   // Violet
  payment_plan_reminder_2d: '#1890ff',
  payment_plan_reminder_1d: '#fa8c16',
  payment_plan_reminder_due: '#ff4d4f',
  activity_report_submit_reminder: '#fa8c16',
  activity_report_validated: '#1890ff',
  activity_report_overdue: '#ff4d4f',
  payment_plan_overdue: '#ff4d4f',
  licence_payment_due_7d: '#faad14',
  licence_payment_due_1d: '#fa8c16',
  licence_payment_blocked: '#ff4d4f',
  messages_clients_ready: '#13c2c2',
  messages_clients_no_phone: '#faad14',
  manufacturing_order_on_hold: '#fa8c16',
  manufacturing_order_done: '#52c41a',
  inventory_audit_started: '#1890ff',
  inventory_audit_to_close: '#fa8c16',
  inventory_loss_created: '#faad14',
  performance_sheets_generated: '#d946ef',
  performance_sheet_validated: '#52c41a',
} as const;

// Icônes pour l'interface
export const NOTIFICATION_ICONS = {
  low_stock: '⚠️',
  out_of_stock: '🚨',
  appointment_reminder_24h: '📅',
  appointment_reminder_2h: '⏰',
  appointment_request: '📞',
  pickup_request: '🧺',
  support_ticket_created: '🎧',
  support_ticket_reply: '💬',
  support_ticket_resolved: '✅',
  support_ticket_opened_for_you: '📩',
  birthday: '🎂',
  inactive_client: '👥',
  loyalty_milestone: '🎁',
  payment_plan_reminder_2d: '📅',
  payment_plan_reminder_1d: '⏰',
  payment_plan_reminder_due: '🚨',
  activity_report_submit_reminder: '📝',
  activity_report_validated: '✅',
  activity_report_overdue: '⏰',
  payment_plan_overdue: '🚨',
  licence_payment_due_7d: '📅',
  licence_payment_due_1d: '⏰',
  licence_payment_blocked: '🔒',
  messages_clients_ready: '💬',
  messages_clients_no_phone: '📱',
  manufacturing_order_on_hold: '⏸️',
  manufacturing_order_done: '🏭',
  inventory_audit_started: '📋',
  inventory_audit_to_close: '✅',
  inventory_loss_created: '📉',
  performance_sheets_generated: '📊',
  performance_sheet_validated: '✅',
} as const;
